import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { orderAPI, gatepassAPI } from '../services/api';
import { trackOrder, untrackOrder, onOrderStatus, onRiderLocation, onRiderNearby } from '../services/socket';
import { toast } from 'react-toastify';
import { FiArrowLeft, FiPhone, FiShare2, FiKey, FiBell, FiMapPin, FiClock, FiPackage, FiMessageSquare, FiX } from 'react-icons/fi';
import './OrderDetail.css';

const STATUS_FLOW = [
  { key: 'placed', label: 'Placed' },
  { key: 'confirmed', label: 'Confirmed' },
  { key: 'preparing', label: 'Preparing' },
  { key: 'picked_up', label: 'Picked Up' },
  { key: 'out_for_delivery', label: 'On the Way' },
  { key: 'delivered', label: 'Delivered' }
];

const OrderDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sharing, setSharing] = useState(false);
  const [generatingPass, setGeneratingPass] = useState(false);
  const [updatingStatus, setUpdatingStatus] = useState(false);
  const [showSMSModal, setShowSMSModal] = useState(false);
  const [smsText, setSmsText] = useState('');
  const [parsingSMS, setParsingSMS] = useState(false);
  const [parsedResult, setParsedResult] = useState(null);

  useEffect(() => {
    fetchOrder();

    // Subscribe to real-time updates
    trackOrder(id);

    onOrderStatus((data) => {
      if (data.orderId === id) {
        setOrder(prev => ({ ...prev, status: data.status, rider: data.rider }));
        toast.info(`Order status: ${data.status.replace(/_/g, ' ')}`);
      }
    });

    onRiderLocation((data) => {
      if (data.orderId === id) {
        setOrder(prev => ({
          ...prev,
          rider: { ...prev.rider, currentLocation: data.location }
        }));
      }
    });

    onRiderNearby((data) => {
      if (data.orderId === id) {
        toast.success(`${data.riderName || 'Rider'} is nearby!`);
      }
    });

    return () => {
      untrackOrder(id);
    };
  }, [id]);

  const fetchOrder = async () => {
    try {
      const response = await orderAPI.getById(id);
      setOrder(response.data.order);
    } catch (error) {
      toast.error('Failed to fetch order');
      navigate('/orders');
    } finally {
      setLoading(false);
    }
  };

  const handleShareInstructions = async () => {
    setSharing(true);
    try {
      await orderAPI.shareInstructions(id);
      toast.success('Instructions shared with rider!');
      fetchOrder();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to share instructions');
    } finally {
      setSharing(false);
    }
  };

  const handleGenerateGatePass = async () => {
    setGeneratingPass(true);
    try {
      await gatepassAPI.generate(id);
      toast.success('Gate pass generated!');
      fetchOrder();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to generate gate pass');
    } finally {
      setGeneratingPass(false);
    }
  };

  const handleNotifySecurity = async () => {
    try {
      await gatepassAPI.notifySecurity(id);
      toast.success('Security has been notified!');
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to notify security');
    }
  };

  const handleStatusChange = async (newStatus) => {
    setUpdatingStatus(true);
    try {
      await orderAPI.updateStatus(id, { status: newStatus });
      setOrder(prev => ({ ...prev, status: newStatus }));
      toast.success(`Status updated to ${newStatus.replace(/_/g, ' ')}`);
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to update status');
    } finally {
      setUpdatingStatus(false);
    }
  };

  const getNextStatus = () => {
    const currentIndex = STATUS_FLOW.findIndex(s => s.key === order?.status);
    if (currentIndex < STATUS_FLOW.length - 1) {
      return STATUS_FLOW[currentIndex + 1];
    }
    return null;
  };

  const handleParseSMS = async () => {
    if (!smsText.trim()) {
      toast.error('Please paste SMS text');
      return;
    }

    setParsingSMS(true);
    setParsedResult(null);

    try {
      const response = await orderAPI.parseSMS(id, smsText);
      if (response.data.success) {
        setParsedResult(response.data.parsed);
        setOrder(response.data.order);
        toast.success('Order updated from SMS!');
        setTimeout(() => {
          setShowSMSModal(false);
          setSmsText('');
          setParsedResult(null);
        }, 2000);
      } else {
        setParsedResult(response.data.parsed);
        toast.error(response.data.message || 'Could not parse SMS');
      }
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to parse SMS');
    } finally {
      setParsingSMS(false);
    }
  };

  const handlePasteFromClipboard = async () => {
    try {
      const text = await navigator.clipboard.readText();
      setSmsText(text);
      toast.success('Pasted from clipboard');
    } catch (error) {
      toast.error('Could not access clipboard. Please paste manually.');
    }
  };

  const getPlatformIcon = (platform) => {
    const icons = { swiggy: '🍔', zomato: '🍕', bigbasket: '🛒', blinkit: '⚡' };
    return icons[platform] || '📦';
  };

  const getStatusColor = (status) => {
    const colors = {
      placed: 'info', confirmed: 'info', preparing: 'warning',
      picked_up: 'warning', out_for_delivery: 'warning',
      near_location: 'success', delivered: 'success', cancelled: 'danger'
    };
    return colors[status] || 'info';
  };

  const getStatusStep = (status) => {
    const steps = ['placed', 'confirmed', 'preparing', 'picked_up', 'out_for_delivery', 'delivered'];
    return steps.indexOf(status) + 1;
  };

  const formatTime = (date) => {
    if (!date) return '-';
    return new Date(date).toLocaleTimeString('en-IN', {
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return <div className="loading"><div className="spinner"></div></div>;
  }

  if (!order) {
    return <div className="container">Order not found</div>;
  }

  return (
    <div className="order-detail-page">
      <div className="container">
        <button className="back-btn" onClick={() => navigate('/orders')}>
          <FiArrowLeft /> Back to Orders
        </button>

        {/* Order Header */}
        <div className="order-header card">
          <div className="order-platform-info">
            <span className="platform-icon">{getPlatformIcon(order.platform)}</span>
            <div>
              <h1>{order.platform}</h1>
              <span className="order-id">#{order.platformOrderId}</span>
            </div>
          </div>
          <span className={`badge badge-${getStatusColor(order.status)} badge-lg`}>
            {order.status.replace(/_/g, ' ')}
          </span>
        </div>

        {/* Status Timeline */}
        <div className="status-timeline card">
          <h2>Order Progress</h2>
          <div className="timeline">
            {['Placed', 'Confirmed', 'Preparing', 'Picked Up', 'On the Way', 'Delivered'].map((step, i) => (
              <div
                key={step}
                className={`timeline-step ${i < getStatusStep(order.status) ? 'completed' : ''} ${i === getStatusStep(order.status) - 1 ? 'current' : ''}`}
              >
                <div className="step-dot" />
                <span className="step-label">{step}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Rider Info */}
        {order.rider?.name && (
          <div className="rider-info card">
            <h2>Delivery Partner</h2>
            <div className="rider-details">
              <div className="rider-avatar">
                {order.rider.name.charAt(0).toUpperCase()}
              </div>
              <div className="rider-text">
                <span className="rider-name">{order.rider.name}</span>
                {order.rider.vehicleNumber && (
                  <span className="rider-vehicle">{order.rider.vehicleNumber}</span>
                )}
              </div>
              {order.rider.phone && (
                <a href={`tel:${order.rider.phone}`} className="call-btn">
                  <FiPhone /> Call
                </a>
              )}
            </div>
          </div>
        )}

        {/* Delivery Location */}
        {order.deliveryLocation && (
          <div className="delivery-location card">
            <h2><FiMapPin /> Delivery Location</h2>
            <div className="location-info">
              <strong>{order.deliveryLocation.label}</strong>
              <p>{order.deliveryLocation.address?.full}</p>
              {order.deliveryLocation.address?.flatNumber && (
                <p className="flat-details">
                  {order.deliveryLocation.address.building},
                  {order.deliveryLocation.address.flatNumber}
                  {order.deliveryLocation.address.floor && `, ${order.deliveryLocation.address.floor}`}
                </p>
              )}
            </div>
          </div>
        )}

        {/* Timing Info */}
        <div className="timing-info card">
          <h2><FiClock /> Timing</h2>
          <div className="timing-grid">
            <div className="timing-item">
              <span className="timing-label">Placed</span>
              <span className="timing-value">{formatTime(order.timing?.placedAt)}</span>
            </div>
            <div className="timing-item">
              <span className="timing-label">Picked Up</span>
              <span className="timing-value">{formatTime(order.timing?.pickedUpAt)}</span>
            </div>
            <div className="timing-item">
              <span className="timing-label">ETA</span>
              <span className="timing-value">{formatTime(order.timing?.estimatedDelivery)}</span>
            </div>
            <div className="timing-item">
              <span className="timing-label">Delivered</span>
              <span className="timing-value">{formatTime(order.timing?.deliveredAt)}</span>
            </div>
          </div>
        </div>

        {/* Order Items */}
        {order.orderDetails?.items?.length > 0 && (
          <div className="order-items card">
            <h2><FiPackage /> Order Items</h2>
            <ul className="items-list">
              {order.orderDetails.items.map((item, i) => (
                <li key={i}>
                  <span>{item.quantity}x {item.name}</span>
                  <span>₹{item.price}</span>
                </li>
              ))}
            </ul>
            <div className="order-total">
              <span>Total</span>
              <span>₹{order.orderDetails.totalAmount}</span>
            </div>
          </div>
        )}

        {/* Status Change */}
        {order.status !== 'delivered' && order.status !== 'cancelled' && (
          <div className="status-change card">
            <h2>Update Order Status</h2>
            <div className="status-buttons">
              {STATUS_FLOW.map((status, index) => {
                const currentIndex = STATUS_FLOW.findIndex(s => s.key === order.status);
                const isCompleted = index < currentIndex;
                const isCurrent = index === currentIndex;
                const isNext = index === currentIndex + 1;

                return (
                  <button
                    key={status.key}
                    className={`status-btn ${isCompleted ? 'completed' : ''} ${isCurrent ? 'current' : ''} ${isNext ? 'next' : ''}`}
                    onClick={() => handleStatusChange(status.key)}
                    disabled={updatingStatus || isCompleted || isCurrent}
                  >
                    {status.label}
                    {isCurrent && <span className="current-badge">Current</span>}
                  </button>
                );
              })}
            </div>
            {getNextStatus() && (
              <button
                className="btn btn-primary btn-block mt-3"
                onClick={() => handleStatusChange(getNextStatus().key)}
                disabled={updatingStatus}
              >
                {updatingStatus ? 'Updating...' : `Move to "${getNextStatus().label}"`}
              </button>
            )}
          </div>
        )}

        {/* Actions */}
        {order.status !== 'delivered' && order.status !== 'cancelled' && (
          <div className="order-actions card">
            <h2>Quick Actions</h2>
            <div className="actions-grid">
              <button
                className="action-btn"
                onClick={handleShareInstructions}
                disabled={sharing || !order.rider?.phone}
              >
                <FiShare2 />
                <span>{order.instructionsShared ? 'Reshare Instructions' : 'Share Instructions'}</span>
                {order.instructionsShared && <span className="action-badge">Sent</span>}
              </button>

              <button
                className="action-btn"
                onClick={handleGenerateGatePass}
                disabled={generatingPass || order.gatePass}
              >
                <FiKey />
                <span>{order.gatePass ? 'Gate Pass Ready' : 'Generate Gate Pass'}</span>
                {order.gatePass && <span className="action-badge">Active</span>}
              </button>

              {order.gatePass && (
                <button className="action-btn" onClick={handleNotifySecurity}>
                  <FiBell />
                  <span>Notify Security</span>
                </button>
              )}
            </div>

            {order.gatePass && (
              <div className="gate-pass-display">
                <span className="pass-label">Gate Pass Code</span>
                <span className="pass-code">{order.gatePass.passCode || 'Loading...'}</span>
              </div>
            )}
          </div>
        )}

        {/* SMS Update Section */}
        {order.status !== 'delivered' && order.status !== 'cancelled' && (
          <div className="sms-update card">
            <h2><FiMessageSquare /> Update from SMS</h2>
            <p className="sms-description">
              Copy delivery SMS from Swiggy/Zomato/etc. and paste here to auto-update order status and rider info.
            </p>
            <button
              className="btn btn-secondary btn-block"
              onClick={() => setShowSMSModal(true)}
            >
              <FiMessageSquare /> Paste Delivery SMS
            </button>
          </div>
        )}
      </div>

      {/* SMS Modal */}
      {showSMSModal && (
        <div className="modal-overlay" onClick={() => setShowSMSModal(false)}>
          <div className="modal sms-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Paste Delivery SMS</h3>
              <button className="modal-close" onClick={() => setShowSMSModal(false)}>
                <FiX />
              </button>
            </div>
            <div className="modal-body">
              <p className="modal-hint">
                Copy the delivery update SMS from your phone and paste it below. We'll extract status, rider info, and ETA automatically.
              </p>
              <div className="sms-input-wrapper">
                <textarea
                  className="sms-textarea"
                  placeholder="Paste SMS here...&#10;&#10;Example: Your Swiggy order is out for delivery! Rider Rajesh (9876543210) is on the way. Arriving in 10 min."
                  value={smsText}
                  onChange={(e) => setSmsText(e.target.value)}
                  rows={5}
                />
                <button
                  className="btn btn-outline paste-btn"
                  onClick={handlePasteFromClipboard}
                  type="button"
                >
                  Paste from Clipboard
                </button>
              </div>

              {parsedResult && (
                <div className={`parse-result ${parsedResult.success ? 'success' : 'error'}`}>
                  <h4>{parsedResult.success ? 'Parsed Successfully!' : 'Could not parse'}</h4>
                  {parsedResult.success ? (
                    <ul className="parsed-info">
                      <li><strong>Platform:</strong> {parsedResult.platform}</li>
                      <li><strong>Status:</strong> {parsedResult.status?.replace(/_/g, ' ')}</li>
                      {parsedResult.rider?.name && <li><strong>Rider:</strong> {parsedResult.rider.name}</li>}
                      {parsedResult.rider?.phone && <li><strong>Phone:</strong> {parsedResult.rider.phone}</li>}
                      {parsedResult.etaMinutes && <li><strong>ETA:</strong> {parsedResult.etaMinutes} minutes</li>}
                    </ul>
                  ) : (
                    <p>{parsedResult.error}</p>
                  )}
                </div>
              )}

              <div className="supported-platforms">
                <span className="label">Supported platforms:</span>
                <div className="platform-badges">
                  <span className="platform-badge swiggy">Swiggy</span>
                  <span className="platform-badge zomato">Zomato</span>
                  <span className="platform-badge blinkit">Blinkit</span>
                  <span className="platform-badge bigbasket">BigBasket</span>
                  <span className="platform-badge amazon">Amazon</span>
                  <span className="platform-badge flipkart">Flipkart</span>
                </div>
              </div>
            </div>
            <div className="modal-footer">
              <button
                className="btn btn-secondary"
                onClick={() => {
                  setShowSMSModal(false);
                  setSmsText('');
                  setParsedResult(null);
                }}
              >
                Cancel
              </button>
              <button
                className="btn btn-primary"
                onClick={handleParseSMS}
                disabled={parsingSMS || !smsText.trim()}
              >
                {parsingSMS ? 'Parsing...' : 'Update Order'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default OrderDetail;
